import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { X, Download } from "lucide-react";
import { CARDS } from "./cards";
import { LOGO_URL } from "./data";
import { API, fireViewContent, fireInitiateCheckout, firePurchase } from "./components";

const CARD_PRICE = 19.00;
const DEDICATION_MAX = 120;

/** Convert card name to a slug (e.g. "Mirror of Light and Shadow" -> "mirror-of-light-and-shadow") */
export function cardSlug(name) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function getCardBySlug(slug) {
  return CARDS.find((c) => cardSlug(c.name) === slug);
}

/** ========== Reusable "Own This Card" button ========== */
export function OwnThisCardButton({ card, variant = "gold", className = "", label }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={variant === "gold"
          ? `btn-gold text-sm tracking-widest ${className}`
          : `btn-outline-gold text-sm tracking-widest ${className}`}
        data-testid={`own-card-btn-${cardSlug(card.name)}`}
      >
        {label || `Own This Card — $${CARD_PRICE.toFixed(0)}`}
      </button>
      {open && (
        <OwnThisCardModal card={card} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

/** ========== Purchase modal ========== */
function OwnThisCardModal({ card, onClose }) {
  const [step, setStep] = useState("form"); // "form" -> "pay" -> "success"
  const [form, setForm] = useState({ name: "", email: "", dedication: "" });
  const [errorMsg, setErrorMsg] = useState("");
  const [downloadToken, setDownloadToken] = useState("");

  const paypalClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;

  // Analytics product shape (matches existing firePurchase pattern)
  const analyticsProduct = {
    id: `card-print-${cardSlug(card.name)}`,
    name: `Mirror Lotus Card Print — ${card.name}`,
    price: CARD_PRICE,
  };

  // Fire ViewContent once when the modal opens
  useEffect(() => {
    fireViewContent && fireViewContent(analyticsProduct);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToPay = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setErrorMsg("Please share your name and email so we know where to send your download.");
      return;
    }
    setErrorMsg("");
    fireInitiateCheckout && fireInitiateCheckout(analyticsProduct);
    setStep("pay");
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center px-4 py-8 bg-black/70 backdrop-blur-sm"
      data-testid="own-card-modal"
      onClick={onClose}
    >
      <div
        className="relative max-w-lg w-full bg-[#2A1140] border border-[#B58B2E]/50 rounded-sm p-6 sm:p-8 shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 text-[#F5D48A]/70 hover:text-[#F5D48A]"
          data-testid="own-card-close"
        >
          <X size={22} />
        </button>

        {step === "form" && (
          <>
            <div className="flex flex-col items-center mb-5">
              <img
                src={card.url}
                alt={card.name}
                className="w-28 rounded-lg mb-3"
                style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.5)" }}
              />
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#B58B2E] mb-1">Own This Card</div>
              <h3 className="serif text-2xl text-[#F5D48A] text-center">{card.name}</h3>
              <p className="text-[#F2EFE8]/75 text-sm text-center mt-3 leading-relaxed">
                Receive two beautifully finished files — a frameable artwork print with your optional
                dedication and Dawn&apos;s signature, plus a full meaning page. Yours forever.
              </p>
            </div>

            <form onSubmit={goToPay} noValidate className="space-y-3" data-testid="own-card-form">
              <input
                type="text"
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3 bg-black/30 border border-[#B58B2E]/40 rounded-sm text-[#F2EFE8] focus:outline-none focus:border-[#F5D48A]"
                data-testid="own-card-name"
              />
              <input
                type="email"
                placeholder="Email (for your downloads)"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 bg-black/30 border border-[#B58B2E]/40 rounded-sm text-[#F2EFE8] focus:outline-none focus:border-[#F5D48A]"
                data-testid="own-card-email"
              />
              <div>
                <textarea
                  placeholder={`Optional dedication (e.g. "For Mom — with love")`}
                  value={form.dedication}
                  onChange={(e) => setForm({ ...form, dedication: e.target.value.slice(0, DEDICATION_MAX) })}
                  className="w-full px-4 py-3 bg-black/30 border border-[#B58B2E]/40 rounded-sm text-[#F2EFE8] focus:outline-none focus:border-[#F5D48A] resize-none"
                  rows="2"
                  data-testid="own-card-dedication"
                />
                <div className="text-[10px] text-[#A89FA8] text-right mt-1">
                  {form.dedication.length} / {DEDICATION_MAX}
                </div>
              </div>
              {errorMsg && <p className="text-[#F0847C] text-sm" data-testid="own-card-error">{errorMsg}</p>}
              <button
                type="submit"
                className="btn-gold w-full mt-2"
                data-testid="own-card-continue"
              >
                Continue to Payment · ${CARD_PRICE.toFixed(2)}
              </button>
              <p className="text-[10px] text-[#A89FA8] text-center pt-2">Secured by PayPal · Instant download</p>
            </form>
          </>
        )}

        {step === "pay" && (
          <div data-testid="own-card-pay">
            <h3 className="serif text-xl text-[#F5D48A] text-center mb-2">Complete your payment</h3>
            <p className="text-[#F2EFE8]/70 text-sm text-center mb-4">
              {card.name} · ${CARD_PRICE.toFixed(2)} · Instant download after PayPal confirms
            </p>
            {paypalClientId ? (
              <PayPalScriptProvider options={{ "client-id": paypalClientId, currency: "USD", intent: "capture" }}>
                <PayPalButtons
                  style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal" }}
                  createOrder={async () => {
                    const res = await axios.post(`${API}/paypal/create-order`, {
                      product_id: `card-print-${cardSlug(card.name)}`,
                      product_name: `Mirror Lotus Card Print — ${card.name}`,
                      price: CARD_PRICE,
                      customer_name: form.name,
                      customer_email: form.email,
                    });
                    return res.data.id;
                  }}
                  onApprove={async (data) => {
                    try {
                      const res = await axios.post(`${API}/card-orders/capture`, {
                        order_id: data.orderID,
                        card_slug: cardSlug(card.name),
                        card_name: card.name,
                        customer_name: form.name,
                        customer_email: form.email,
                        dedication: form.dedication,
                      });
                      setDownloadToken(res.data.download_token);
                      // Fire GA4 `purchase` + Meta Pixel `Purchase` events only on real capture success
                      firePurchase && firePurchase(analyticsProduct, data.orderID);
                      setStep("success");
                    } catch (err) {
                      setErrorMsg("Payment succeeded but we couldn't finalize your download. Please contact Dawn.");
                    }
                  }}
                  onError={() => setErrorMsg("PayPal couldn't complete this payment. Please try again.")}
                />
              </PayPalScriptProvider>
            ) : (
              <p className="text-[#F0847C] text-sm text-center">Payment temporarily unavailable.</p>
            )}
            {errorMsg && <p className="text-[#F0847C] text-sm mt-3" data-testid="own-card-pay-error">{errorMsg}</p>}
            <button
              onClick={() => setStep("form")}
              className="text-[#A89FA8] text-xs mt-4 mx-auto block hover:text-[#F5D48A]"
              data-testid="own-card-back"
            >
              ← Back
            </button>
          </div>
        )}

        {step === "success" && (
          <div className="text-center py-4" data-testid="own-card-success">
            <div className="text-5xl mb-3">🌸</div>
            <h3 className="serif text-2xl text-[#F5D48A] mb-3">Thank you, {form.name.split(" ")[0]}.</h3>
            <p className="text-[#F2EFE8]/80 leading-relaxed mb-6">
              Your {card.name} card is ready. Two beautifully finished files — one to frame, one to hold.
            </p>
            <Link
              to={`/download/card/${downloadToken}`}
              className="btn-gold inline-block"
              data-testid="own-card-goto-downloads"
            >
              View My Downloads →
            </Link>
            <p className="text-[10px] text-[#A89FA8] mt-4 tracking-widest">Download code · {downloadToken}</p>
          </div>
        )}
      </div>
    </div>
  );
}


/** ========== Card Downloads landing page (/download/card/:token) ========== */
export function CardDownloadPage() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API}/card-orders/token/${token}`)
      .then((r) => setOrder(r.data))
      .catch(() => setNotFound(true));
  }, [token]);

  if (notFound) {
    return (
      <section className="min-h-screen pt-32 pb-16 px-6 text-center" data-testid="card-download-notfound">
        <h1 className="display-serif text-3xl mb-4 text-[#F5D48A]">Download not found</h1>
        <p className="text-[#F2EFE8]/70 mb-6">The code you tried doesn&apos;t match any order.</p>
        <Link to="/mirror-lotus" className="btn-outline-gold">Return to Mirror Lotus</Link>
      </section>
    );
  }
  if (!order) {
    return <section className="min-h-screen pt-32 text-center text-[#A89FA8]">Loading…</section>;
  }

  return (
    <section className="min-h-screen pt-32 pb-20 px-6 lg:px-12" data-testid="card-download-page">
      <div className="max-w-2xl mx-auto text-center">
        <div className="section-label mb-4">Your Downloads</div>
        <h1 className="display-serif text-4xl md:text-5xl mb-3 text-[#F5D48A]">{order.card_name}</h1>
        <p className="text-[#F2EFE8]/70 mb-10">
          Two files, ready when you are. Open each one in Safari, then press <kbd>⌘</kbd>+<kbd>P</kbd> → <em>Save as PDF</em>.
        </p>
        <div className="grid sm:grid-cols-2 gap-6 mb-10">
          <a
            href={`/download/card/${token}/artwork`}
            target="_blank"
            rel="noreferrer"
            className="sf-card jewel-card jewel-gold rounded-sm p-6 hover:brightness-110 transition text-left"
            data-testid="dl-artwork"
          >
            <Download className="text-[#F5D48A] mb-3" />
            <h3 className="serif text-xl text-[#F5D48A] mb-1">Frameable Artwork</h3>
            <p className="text-[#F2EFE8]/70 text-sm">Your card, your dedication, Dawn&apos;s signature — ready to print and frame.</p>
          </a>
          <a
            href={`/download/card/${token}/meaning`}
            target="_blank"
            rel="noreferrer"
            className="sf-card jewel-card jewel-gold rounded-sm p-6 hover:brightness-110 transition text-left"
            data-testid="dl-meaning"
          >
            <Download className="text-[#F5D48A] mb-3" />
            <h3 className="serif text-xl text-[#F5D48A] mb-1">Card Meaning</h3>
            <p className="text-[#F2EFE8]/70 text-sm">A beautifully formatted page with your card&apos;s essence and reflection.</p>
          </a>
        </div>
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#A89FA8]">
          Save this page — Download code {token}
        </p>
      </div>
    </section>
  );
}


/** ========== Print-ready Artwork page (/download/card/:token/artwork) ========== */
export function ArtworkPrintPage() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    document.body.classList.add("print-mode");
    axios.get(`${API}/card-orders/token/${token}`)
      .then((r) => setOrder(r.data))
      .catch(() => setNotFound(true));
    return () => document.body.classList.remove("print-mode");
  }, [token]);

  if (notFound) return <div style={{padding: "60px", textAlign: "center"}}>Download not found</div>;
  if (!order) return <div style={{padding: "60px", textAlign: "center", color: "#666"}}>Loading…</div>;

  const card = getCardBySlug(order.card_slug);
  if (!card) return <div style={{padding: "60px"}}>Card artwork unavailable</div>;

  return (
    <div className="card-artwork-print" data-testid="artwork-print">
      <div className="card-artwork-inner">
        <img src={card.url} alt={card.name} className="card-artwork-img" />
        <h1 className="card-artwork-name" data-testid="artwork-card-name">{card.name}</h1>
        <p className="card-artwork-essence">&ldquo;{card.essence}&rdquo;</p>
        {order.dedication && (
          <p className="card-artwork-dedication" data-testid="artwork-dedication">
            {order.dedication}
          </p>
        )}
        <div className="card-artwork-signature" data-testid="artwork-signature">Dawn Delisle</div>
        <div className="card-artwork-brand">SNAPSHOTFAME · MIRROR LOTUS</div>
      </div>
      <div className="print-screen-only">
        <button onClick={() => window.print()} className="print-download-btn" data-testid="artwork-save-pdf">
          Save as PDF
        </button>
      </div>
    </div>
  );
}


/** ========== Print-ready Meaning page (/download/card/:token/meaning) ========== */
export function MeaningPrintPage() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    document.body.classList.add("print-mode");
    axios.get(`${API}/card-orders/token/${token}`)
      .then((r) => setOrder(r.data))
      .catch(() => setNotFound(true));
    return () => document.body.classList.remove("print-mode");
  }, [token]);

  if (notFound) return <div style={{padding: "60px", textAlign: "center"}}>Download not found</div>;
  if (!order) return <div style={{padding: "60px", textAlign: "center", color: "#666"}}>Loading…</div>;

  const card = getCardBySlug(order.card_slug);
  if (!card) return <div style={{padding: "60px"}}>Card meaning unavailable</div>;

  return (
    <div className="card-meaning-print" data-testid="meaning-print">
      <div className="card-meaning-inner">
        <img src={LOGO_URL} alt="SnapshotFame" className="card-meaning-logo" />
        <div className="card-meaning-brand">SNAPSHOTFAME · MIRROR LOTUS GUIDE</div>
        <img src={card.url} alt={card.name} className="card-meaning-img" />
        <h1 className="card-meaning-name" data-testid="meaning-card-name">{card.name}</h1>
        <div className="card-meaning-essence-label">Essence</div>
        <p className="card-meaning-essence">&ldquo;{card.essence}&rdquo;</p>
        <div className="card-meaning-prompt-label">Reflection</div>
        <p className="card-meaning-prompt">{card.prompt}</p>
        {order.dedication && (
          <div className="card-meaning-dedication">
            <div className="card-meaning-dedication-label">Dedicated</div>
            <p>{order.dedication}</p>
          </div>
        )}
        <div className="card-meaning-signature">Dawn Delisle</div>
        <div className="card-meaning-footer">A private keepsake · snapshotfame.com</div>
      </div>
      <div className="print-screen-only">
        <button onClick={() => window.print()} className="print-download-btn" data-testid="meaning-save-pdf">
          Save as PDF
        </button>
      </div>
    </div>
  );
}
